export type Point = [number, number];
export type IllustrationShape =
  | { type: 'line' | 'arrow'; x1: number; y1: number; x2: number; y2: number; dashed?: boolean }
  | { type: 'rect'; x: number; y: number; width: number; height: number; fill?: 'none' | 'light' | 'dark' }
  | { type: 'ellipse'; cx: number; cy: number; rx: number; ry: number; fill?: 'none' | 'light' | 'dark' }
  | { type: 'polygon'; points: Point[]; fill?: 'none' | 'light' | 'dark' }
  | { type: 'text'; x: number; y: number; text: string; anchor?: 'start' | 'middle' | 'end' };
export type IllustrationVisual = { kind: 'illustration'; title: string; caption: string; shapes: IllustrationShape[] };
export type TeachingVisual =
  | IllustrationVisual
  | { kind: 'graph'; title: string; caption: string; xLabel: string; yLabel: string; xRange: Point; yRange: Point; series: { label: string; points: Point[] }[]; vectors: { label: string; from: Point; to: Point }[] }
  | { kind: 'diagram'; title: string; caption: string; nodes: { id: string; label: string; x: number; y: number }[]; edges: { from: string; to: string; label: string }[] };

const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown, max = 180): string => typeof v === 'string' ? v.slice(0, max) : '';
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= 1e6;
const point = (v: unknown): v is Point => Array.isArray(v) && v.length === 2 && v.every(finite);
const range = (v: unknown): v is Point => point(v) && v[1] > v[0] && v[1] - v[0] >= .00001;

function illustrationShape(v: unknown): IllustrationShape {
  if (!record(v)) throw Error('Invalid shape');
  const number = (key: string, max: number) => {
    const n = v[key];
    if (!finite(n) || n < 0 || n > max) throw Error('Invalid coordinate');
    return n;
  };
  const fill = v.fill === 'light' || v.fill === 'dark' ? v.fill : 'none';
  if (v.type === 'line' || v.type === 'arrow') {
    return { type: v.type, x1: number('x1', 600), y1: number('y1', 400), x2: number('x2', 600), y2: number('y2', 400), dashed: v.dashed === true };
  }
  if (v.type === 'rect') {
    const x = number('x', 600), y = number('y', 400), width = number('width', 600), height = number('height', 400);
    if (!width || !height || x + width > 600 || y + height > 400) throw Error('Shape outside canvas');
    return { type: 'rect', x, y, width, height, fill };
  }
  if (v.type === 'ellipse') {
    const cx = number('cx', 600), cy = number('cy', 400), rx = number('rx', 300), ry = number('ry', 200);
    if (!rx || !ry || cx - rx < 0 || cx + rx > 600 || cy - ry < 0 || cy + ry > 400) throw Error('Shape outside canvas');
    return { type: 'ellipse', cx, cy, rx, ry, fill };
  }
  if (v.type === 'polygon') {
    if (!Array.isArray(v.points) || v.points.length < 3 || v.points.length > 24 ||
      !v.points.every(p => point(p) && p[0] >= 0 && p[0] <= 600 && p[1] >= 0 && p[1] <= 400)) throw Error('Invalid polygon');
    return { type: 'polygon', points: v.points as Point[], fill };
  }
  if (v.type === 'text' && typeof v.text === 'string' && v.text.trim() && v.text.length <= 60) {
    return { type: 'text', x: number('x', 600), y: number('y', 400), text: v.text,
      anchor: v.anchor === 'start' || v.anchor === 'end' ? v.anchor : 'middle' };
  }
  throw Error('Unsupported shape');
}

/** Model output is data only: never execute expressions, markup, or SVG from a response. */
export function parseTeachingVisual(source: string): TeachingVisual | null {
  if (source.length > 40000) return null;
  try {
    const v: unknown = JSON.parse(source);
    if (!record(v) || !text(v.title)) return null;
    const common = { title: text(v.title), caption: text(v.caption, 500) };
    if (v.kind === 'illustration' && Array.isArray(v.shapes) && v.shapes.length > 0 && v.shapes.length <= 60) {
      const shapes = v.shapes.map(illustrationShape);
      if (!common.caption || !shapes.some(s => s.type === 'text') || !shapes.some(s => s.type !== 'text')) return null;
      return { kind: 'illustration', ...common, shapes };
    }
    if (v.kind === 'graph' && range(v.xRange) && range(v.yRange)) {
      const xRange = v.xRange;
      const yRange = v.yRange;
      const inside = (p: unknown): p is Point => point(p) && p[0] >= xRange[0] && p[0] <= xRange[1] && p[1] >= yRange[0] && p[1] <= yRange[1];
      const series = Array.isArray(v.series) ? v.series.slice(0, 4).map(s => {
        if (!record(s) || !text(s.label) || !Array.isArray(s.points) || s.points.length < 2 || s.points.length > 160 || !s.points.every(inside)) throw Error('Invalid series');
        return { label: text(s.label, 70), points: s.points as Point[] };
      }) : [];
      const vectors = Array.isArray(v.vectors) ? v.vectors.slice(0, 6).map(s => {
        if (!record(s) || !text(s.label) || !inside(s.from) || !inside(s.to)) throw Error('Invalid vector');
        return { label: text(s.label, 70), from: s.from, to: s.to };
      }) : [];
      if (!series.length && !vectors.length) return null;
      return { kind: 'graph', ...common, xLabel: text(v.xLabel, 60) || 'x', yLabel: text(v.yLabel, 60) || 'y', xRange, yRange, series, vectors };
    }
    if (v.kind === 'diagram' && Array.isArray(v.nodes) && v.nodes.length > 0 && v.nodes.length <= 8) {
      const ids = new Set<string>();
      const nodes = v.nodes.map(n => {
        if (!record(n) || !text(n.id) || ids.has(text(n.id)) || !text(n.label) || !finite(n.x) || !finite(n.y) || n.x < 0 || n.x > 100 || n.y < 0 || n.y > 100) throw Error('Invalid node');
        ids.add(text(n.id));
        return { id: text(n.id), label: text(n.label, 65), x: n.x, y: n.y };
      });
      const edges = Array.isArray(v.edges) ? v.edges.slice(0, 12).map(e => {
        if (!record(e) || !ids.has(text(e.from)) || !ids.has(text(e.to))) throw Error('Invalid edge');
        return { from: text(e.from), to: text(e.to), label: text(e.label, 50) };
      }) : [];
      return { kind: 'diagram', ...common, nodes, edges };
    }
    return null;
  } catch {
    return null;
  }
}